import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Globe, Send, Check, AlertCircle } from 'lucide-react';
import { SectionHeader } from '../components/SectionHeader';
import { BRAND, COMPANIES } from '../data/content';
import { CONTACT } from '../constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: '',
  email: '',
  company: '',
  phone: '',
  subject: '',
  message: '',
  interest: '',
};

const INTERESTS = [
  '',
  'Civil Infrastructure',
  'Industrial EPC',
  'Textile Engineering',
  'Welding Automation',
  'Technology & SaaS',
  'Hospitality',
  'Partnership / Vendor',
  'Press / Media',
  'Other',
];

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'submitting', message: '' });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: 'success',
        message: 'Thank you. Your message has been received — our team will respond shortly.',
      });
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        'Could not submit. Please try again or email info@clangrp.com.';
      setStatus({ state: 'error', message: typeof msg === 'string' ? msg : 'Submission failed.' });
    }
  };

  return (
    <div className="bg-white">
      <section className="relative pt-44 pb-20 bg-[#0D1B2A] grain">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="overline text-white/80 flex items-center gap-3 mb-6">
            <span className="gold-rule" /> Contact
          </div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            Let&apos;s <span className="text-[#D4AF37]">engineer</span> what&apos;s next.
          </h1>
          <p className="mt-8 text-white/75 text-lg max-w-3xl leading-relaxed">
            Tell us about your project, partnership or capability requirement. Our team responds
            within one business day.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader overline="Reach Us" title="Headquartered in Mumbai. Operating globally." />
            <div className="mt-10 space-y-5 text-[15px] text-[#0D1B2A]">
              <InfoRow icon={MapPin} label="Headquarters" value={`${BRAND.hq}`} />
              <InfoRow icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} />
              <InfoRow icon={Globe} label="Website" value={BRAND.website} />
              <InfoRow icon={Phone} label="Operating Regions" value={BRAND.regions.join(' · ')} />
            </div>

            <div className="mt-12 bg-[#F5F7FA] p-7 border-l-2 border-[#D4AF37]">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#D4AF37] mb-4">
                Group Companies
              </div>
              <div className="grid grid-cols-2 gap-2 text-[13px] text-[#0D1B2A]">
                {COMPANIES.map((c) => (
                  <div key={c.slug} className="font-display font-semibold">
                    {c.name}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-[#0D1B2A]/10 p-7 md:p-10">
              <div className="overline mb-3 flex items-center gap-3">
                <span className="gold-rule" /> Send a Message
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0D1B2A]">
                Tell us about your project
              </h2>
              <form onSubmit={onSubmit} data-testid={CONTACT.form} className="mt-8 grid sm:grid-cols-2 gap-5">
                <Field name="name" label="Full Name *" testId={CONTACT.name} value={form.name} onChange={onChange} required />
                <Field name="email" type="email" label="Email *" testId={CONTACT.email} value={form.email} onChange={onChange} required />
                <Field name="company" label="Company" testId={CONTACT.company} value={form.company} onChange={onChange} />
                <Field name="phone" label="Phone" testId={CONTACT.phone} value={form.phone} onChange={onChange} />
                <div className="sm:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0D1B2A] mb-2">
                    Area of Interest
                  </label>
                  <select
                    name="interest"
                    value={form.interest}
                    onChange={onChange}
                    data-testid={CONTACT.interest}
                    className="w-full border border-[#0D1B2A]/15 px-4 py-3 text-[15px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none bg-white"
                  >
                    {INTERESTS.map((i) => (
                      <option key={i || 'none'} value={i}>
                        {i || 'Select area of interest'}
                      </option>
                    ))}
                  </select>
                </div>
                <Field name="subject" label="Subject *" testId={CONTACT.subject} value={form.subject} onChange={onChange} required className="sm:col-span-2" />
                <div className="sm:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0D1B2A] mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    data-testid={CONTACT.message}
                    value={form.message}
                    onChange={onChange}
                    rows={6}
                    required
                    className="w-full border border-[#0D1B2A]/15 px-4 py-3 text-[15px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none"
                  />
                </div>
                <div className="sm:col-span-2 pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    data-testid={CONTACT.submit}
                    disabled={status.state === 'submitting'}
                    className="btn-primary justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status.state === 'submitting' ? 'Sending...' : 'Send Message'} <Send size={15} />
                  </button>
                  {status.state === 'success' && (
                    <motion.div
                      data-testid={CONTACT.success}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="text-sm text-[#16A34A] inline-flex items-center gap-2"
                    >
                      <Check size={16} /> {status.message}
                    </motion.div>
                  )}
                  {status.state === 'error' && (
                    <div data-testid={CONTACT.error} className="text-sm text-red-600 inline-flex items-center gap-2">
                      <AlertCircle size={16} /> {status.message}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="w-10 h-10 grid place-items-center border border-[#D4AF37]/40 shrink-0">
        <Icon size={16} className="text-[#D4AF37]" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#4B5563]">
          {label}
        </div>
        <div className="font-display font-semibold text-[#0D1B2A] mt-0.5 group-hover:text-[#D4AF37] transition-colors">
          {value}
        </div>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : <div>{inner}</div>;
}

function Field({ name, label, value, onChange, testId, type = 'text', required, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0D1B2A] mb-2">
        {label}
      </label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full border border-[#0D1B2A]/15 px-4 py-3 text-[15px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none"
      />
    </div>
  );
}
